import React, { useEffect, useState } from "react";
import { Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import IsoDatePicker from "../../../components/IsoDatePicker";
import {
  addBookingEpass,
  fetchBookingEpassFile,
  listEpasses,
  removeBookingEpass,
  removeBookingEpassFile,
  updateBookingEpass,
  uploadBookingEpass,
} from "../../../services/epassApi";
import { formatDateDMY, formatDateTimeDMY } from "../../../utils/dateFormat";
import { openFetchedFile } from "../../../utils/openFetchedFile";
import {
  EPASS_ACCEPT,
  EPASS_NOT_NEEDED,
  EPASS_UPLOADED,
  epassDone,
  epassFileProblem,
  epassStatusLabel,
  epassStatusVariant,
} from "../../../utils/epassStatus";

/** A pending e-pass holds the booking back from Ready, like pending hotels or driver. */
export function bookingEpassesPending(booking) {
  return (booking?.epasses || []).some((epass) => !epassDone(epass));
}

export function bookingEpassFilterValues(booking) {
  const epasses = booking?.epasses || [];
  if (!epasses.length) return [];
  return [bookingEpassesPending(booking) ? "E-pass pending" : "E-pass done"];
}

/**
 * The e-passes in a booking's Trip Preparation cell: status, upload and open. Dates, "not
 * needed" and e-passes added by hand are changed in the Manage window, so the cell stays short.
 */
export default function BookingEpassChecklist({ booking, onPatched }) {
  const [busy, setBusy] = useState("");
  const [managing, setManaging] = useState(false);
  const epasses = booking?.epasses || [];

  const run = async (key, action) => {
    setBusy(key);
    try {
      const data = await action();
      if (data?.id) onPatched(data);
    } catch (error) {
      alert(error?.response?.data?.message || "Could not update the e-pass.");
    } finally {
      setBusy("");
    }
  };

  const upload = (epass, file) => {
    const problem = epassFileProblem(file);
    if (problem) {
      alert(problem);
      return;
    }
    run(`upload-${epass.epassId}`, () => uploadBookingEpass(booking.id, epass.epassId, file));
  };

  return (
    <div className="small w-100 mt-1">
      {epasses.map((epass) => (
        <div key={epass.epassId} className="border-top pt-1 mt-1">
          <div className="d-flex flex-wrap align-items-center gap-1">
            <Badge bg={epassStatusVariant(epass.status)}>E-pass: {epassStatusLabel(epass.status)}</Badge>
          </div>
          <div className="fw-semibold">{epass.name}</div>
          <div className="text-muted">
            <EpassWhen epass={epass} />
          </div>
          {epass.status === EPASS_UPLOADED && (
            <div className="text-muted">
              By {epass.uploadedBy || "-"}{epass.uploadedAt ? `, ${formatDateTimeDMY(epass.uploadedAt, "")}` : ""}
            </div>
          )}
          <div className="d-flex flex-wrap gap-1 mt-1">
            <EpassUploadButton
              label={epass.fileName ? "Replace" : "Upload"}
              busy={busy === `upload-${epass.epassId}`}
              onFile={(file) => upload(epass, file)}
            />
            {epass.fileName && <EpassOpenButtons bookingId={booking.id} epass={epass} />}
          </div>
        </div>
      ))}
      <Button variant="link" size="sm" className="p-0 mt-1" onClick={() => setManaging(true)}>
        {epasses.length ? "Manage e-passes" : "+ E-pass"}
      </Button>
      {managing && (
        <ManageEpassesModal booking={booking} onPatched={onPatched} onHide={() => setManaging(false)} />
      )}
    </div>
  );
}

function EpassWhen({ epass }) {
  const date = epass.neededOn ? formatDateDMY(epass.neededOn, "") : "No date";
  const day = epass.dayNumber ? `Day ${epass.dayNumber} · ` : "";
  return (
    <>
      {epass.dateChanged ? "" : day}{date}
      {epass.dateChanged && " (date changed)"}
      {epass.manual && " (added on booking)"}
    </>
  );
}

function EpassUploadButton({ label, busy, onFile }) {
  return (
    <label className={`btn btn-sm btn-outline-primary py-0 px-2 mb-0${busy ? " disabled" : ""}`}>
      {busy ? <Spinner size="sm" animation="border" /> : label}
      <input
        type="file"
        hidden
        accept={EPASS_ACCEPT}
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so picking the same file again still fires.
          event.target.value = "";
          if (file) onFile(file);
        }}
      />
    </label>
  );
}

function EpassOpenButtons({ bookingId, epass }) {
  const open = (download) =>
    openFetchedFile(() => fetchBookingEpassFile(bookingId, epass.epassId), epass.fileName || "epass", download)
      .catch(() => alert("Could not open the e-pass."));
  return (
    <>
      <Button size="sm" variant="outline-secondary" className="py-0 px-2" onClick={() => open(false)}>View</Button>
      <Button size="sm" variant="outline-secondary" className="py-0 px-2" onClick={() => open(true)}>Download</Button>
    </>
  );
}

function ManageEpassesModal({ booking, onPatched, onHide }) {
  const [busy, setBusy] = useState("");
  const [options, setOptions] = useState(null);
  const [newEpassId, setNewEpassId] = useState("");
  const [newDate, setNewDate] = useState(booking.travelDate || "");
  const epasses = booking.epasses || [];

  const run = async (key, action) => {
    setBusy(key);
    try {
      const data = await action();
      if (data?.id) onPatched(data);
      return true;
    } catch (error) {
      alert(error?.response?.data?.message || "Could not update the e-pass.");
      return false;
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    listEpasses()
      .then((data) => setOptions((Array.isArray(data) ? data : []).filter((epass) => epass.active !== false)))
      .catch((error) => {
        setOptions([]);
        alert(error?.response?.data?.message || "Could not load the e-pass list.");
      });
  }, []);

  const listedIds = new Set(epasses.map((epass) => String(epass.epassId)));
  const addable = (options || []).filter((epass) => !listedIds.has(String(epass.id)));

  const add = async () => {
    const ok = await run("add", () => addBookingEpass(booking.id, Number(newEpassId), newDate || null));
    if (ok) setNewEpassId("");
  };

  return (
    <Modal show onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>E-passes · Booking #{booking.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!epasses.length && <div className="text-muted mb-3">This booking's trip needs no e-pass.</div>}
        {epasses.map((epass) => (
          <EpassRow
            key={epass.epassId}
            booking={booking}
            epass={epass}
            busy={busy}
            onSave={(changes) => run(`save-${epass.epassId}`, () => updateBookingEpass(booking.id, epass.epassId, changes))}
            onRemoveFile={() => {
              if (!window.confirm(`Remove the uploaded file for ${epass.name}?`)) return;
              run(`file-${epass.epassId}`, () => removeBookingEpassFile(booking.id, epass.epassId));
            }}
            onRemove={() => {
              if (!window.confirm(`Take ${epass.name} off this booking?`)) return;
              run(`remove-${epass.epassId}`, () => removeBookingEpass(booking.id, epass.epassId));
            }}
          />
        ))}

        <div className="border rounded p-2 mt-3">
          <div className="fw-semibold mb-2">Add an e-pass to this booking</div>
          <div className="small text-muted mb-2">For a custom trip or a changed route. Trip e-passes come from Dynamic Pricing.</div>
          <div className="d-flex flex-wrap gap-2 align-items-end">
            <div style={{ minWidth: 220 }}>
              <Form.Label className="small mb-1">E-pass</Form.Label>
              <Form.Select size="sm" value={newEpassId} onChange={(e) => setNewEpassId(e.target.value)} disabled={options == null}>
                <option value="">{options == null ? "Loading..." : addable.length ? "Choose" : "None left to add"}</option>
                {addable.map((epass) => (
                  <option key={epass.id} value={epass.id}>{epass.name}</option>
                ))}
              </Form.Select>
            </div>
            <div style={{ width: 150 }}>
              <Form.Label className="small mb-1">Needed on</Form.Label>
              <IsoDatePicker value={newDate} onChange={setNewDate} className="form-control form-control-sm" />
            </div>
            <Button size="sm" disabled={!newEpassId || !newDate || busy === "add"} onClick={add}>
              {busy === "add" ? "Adding..." : "Add"}
            </Button>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

function EpassRow({ booking, epass, busy, onSave, onRemoveFile, onRemove }) {
  const [neededOn, setNeededOn] = useState(epass.neededOn || "");
  const [notNeeded, setNotNeeded] = useState(epass.status === EPASS_NOT_NEEDED);
  const changed = neededOn !== (epass.neededOn || "") || notNeeded !== (epass.status === EPASS_NOT_NEEDED);
  const tripDate = epass.dayNumber && booking.travelDate ? addDays(booking.travelDate, epass.dayNumber - 1) : "";

  return (
    <div className="border rounded p-2 mb-2">
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <div>
          <div className="fw-semibold">
            {epass.name}{" "}
            <Badge bg={epassStatusVariant(epass.status)}>{epassStatusLabel(epass.status)}</Badge>
          </div>
          <div className="small text-muted">
            {epass.manual ? "Added on this booking" : epass.dayNumber ? `Trip: package Day ${epass.dayNumber}` : "From an earlier trip setup"}
            {epass.fileName ? ` · File: ${epass.fileName}` : ""}
          </div>
        </div>
        {epass.applyLink && (
          <Button as="a" href={epass.applyLink} target="_blank" rel="noreferrer" size="sm" variant="outline-primary" className="align-self-start">
            Apply
          </Button>
        )}
      </div>
      {epass.instructions && <div className="small text-muted mt-1" style={{ whiteSpace: "pre-line" }}>{epass.instructions}</div>}
      <div className="d-flex flex-wrap gap-2 align-items-end mt-2">
        <div style={{ width: 150 }}>
          <Form.Label className="small mb-1">Needed on</Form.Label>
          <IsoDatePicker value={neededOn} onChange={setNeededOn} className="form-control form-control-sm" />
        </div>
        {tripDate && neededOn !== tripDate && (
          <Button size="sm" variant="link" className="p-0 mb-1" onClick={() => setNeededOn(tripDate)}>
            Back to Day {epass.dayNumber}
          </Button>
        )}
        {!epass.manual && (
          <Form.Check
            type="checkbox"
            className="small mb-1"
            id={`epass-not-needed-${booking.id}-${epass.epassId}`}
            label="Not needed for this booking"
            checked={notNeeded}
            onChange={(e) => setNotNeeded(e.target.checked)}
          />
        )}
        <Button
          size="sm"
          disabled={!changed || busy === `save-${epass.epassId}`}
          onClick={() => onSave({ neededOn: neededOn || null, notNeeded })}
        >
          {busy === `save-${epass.epassId}` ? "Saving..." : "Save"}
        </Button>
        {epass.fileName && (
          <Button size="sm" variant="outline-danger" disabled={busy === `file-${epass.epassId}`} onClick={onRemoveFile}>
            Remove file
          </Button>
        )}
        {epass.manual && (
          <Button size="sm" variant="outline-danger" disabled={busy === `remove-${epass.epassId}`} onClick={onRemove}>
            Take off booking
          </Button>
        )}
      </div>
    </div>
  );
}

function addDays(iso, days) {
  const [year, month, day] = String(iso).slice(0, 10).split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().slice(0, 10);
}
